import { create } from 'zustand';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';

type UserRole = 'superadmin' | 'admin' | 'referent' | 'member';

interface OrgSummary {
	id: string;
	nome: string;
	/** Ruolo dell'utente in questa CER (per-membership). */
	role: UserRole;
}

interface MeResponse {
	userId: string;
	email: string;
	fullName: string | null;
	avatarUrl: string | null;
	createdAt: string | null;
	/** Effective max role: superadmin global, else the highest per-CER membership role. */
	role: UserRole;
	availableOrgs: OrgSummary[];
	memberTenantIds: string[];
	/** Consenso a comparire per nome nelle classifiche dei soci (INB-357). */
	classificaConsenso?: boolean;
}

interface AuthState {
	userId: string | null;
	email: string | null;
	fullName: string | null;
	avatarUrl: string | null;
	createdAt: string | null;
	role: UserRole | null;
	availableOrgs: OrgSummary[];
	memberTenantIds: string[];
	classificaConsenso: boolean;
	loaded: boolean;

	fetchAuth: () => Promise<void>;
	signOut: () => Promise<void>;
	/** Svuota il contesto senza toccare Supabase (sessione già finita lato client). */
	clear: () => void;
	setFullName: (fullName: string | null) => void;
	setAvatarUrl: (url: string | null) => void;
	setClassificaConsenso: (value: boolean) => void;
}

const CLEARED: Omit<
	AuthState,
	'fetchAuth' | 'signOut' | 'clear' | 'setFullName' | 'setAvatarUrl' | 'setClassificaConsenso'
> = {
	userId: null,
	email: null,
	fullName: null,
	avatarUrl: null,
	createdAt: null,
	role: null,
	availableOrgs: [],
	memberTenantIds: [],
	classificaConsenso: false,
	loaded: true,
};

export const useAuthStore = create<AuthState>((set) => ({
	...CLEARED,
	loaded: false,

	fetchAuth: async () => {
		try {
			const data = await api<MeResponse>('/auth/me');
			set({
				userId: data.userId,
				email: data.email,
				fullName: data.fullName ?? null,
				avatarUrl: data.avatarUrl ?? null,
				createdAt: data.createdAt ?? null,
				role: data.role,
				availableOrgs: data.availableOrgs ?? [],
				memberTenantIds: data.memberTenantIds ?? [],
				classificaConsenso: data.classificaConsenso === true,
				loaded: true,
			});
		} catch {
			set({ ...CLEARED });
		}
	},

	signOut: async () => {
		await supabase.auth.signOut();
		set({ ...CLEARED });
	},

	clear: () => set({ ...CLEARED }),

	setFullName: (fullName) => set({ fullName }),
	setAvatarUrl: (url) => set({ avatarUrl: url }),
	setClassificaConsenso: (value) => set({ classificaConsenso: value }),
}));
